import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import {
  projectPlanarUvs,
  projectRoundedBoxUvs,
  roundedBox,
  standardMaterial,
} from "../render/materials";
import { disposeVehicleTextures, type VehicleTextures } from "./vehicleTextures";

export type CivilianVehicleType =
  | "compact"
  | "hatchback"
  | "sedan"
  | "coupe"
  | "wagon"
  | "crossover"
  | "suv"
  | "minivan"
  | "pickup"
  | "cargo-van";
export type VehicleType = CivilianVehicleType | "taxi" | "police";

export const CIVILIAN_VEHICLE_TYPES = [
  "compact",
  "hatchback",
  "sedan",
  "coupe",
  "wagon",
  "crossover",
  "suv",
  "minivan",
  "pickup",
  "cargo-van",
] as const satisfies readonly CivilianVehicleType[];

export const CIVILIAN_VEHICLE_COLORS = [
  0xb94f43, 0xd1aa38, 0x335f76, 0xd7d4c6, 0x3f6652, 0x343b3d, 0x92999a, 0x3f5796,
] as const;

export const TRAFFIC_FLEET_SEQUENCE = [
  "sedan",
  "crossover",
  "taxi",
  "compact",
  "suv",
  "hatchback",
  "pickup",
  "wagon",
  "taxi",
  "coupe",
  "minivan",
  "cargo-van",
  "police",
  "sedan",
  "crossover",
  "compact",
  "taxi",
  "suv",
  "hatchback",
  "pickup",
  "wagon",
  "coupe",
  "minivan",
  "cargo-van",
] as const satisfies readonly VehicleType[];

export type TrafficAppearance =
  | { type: CivilianVehicleType; color: number }
  | { type: "taxi" | "police" };

const CIVILIAN_TYPES = new Set<VehicleType>(CIVILIAN_VEHICLE_TYPES);

export function trafficAppearanceAt(index: number): TrafficAppearance {
  const normalizedIndex =
    ((Math.trunc(index) % TRAFFIC_FLEET_SEQUENCE.length) + TRAFFIC_FLEET_SEQUENCE.length) %
    TRAFFIC_FLEET_SEQUENCE.length;
  const type = TRAFFIC_FLEET_SEQUENCE[normalizedIndex]!;
  if (type === "taxi" || type === "police") return { type };
  return {
    type,
    color: CIVILIAN_VEHICLE_COLORS[(normalizedIndex * 5) % CIVILIAN_VEHICLE_COLORS.length]!,
  };
}

type VehicleProfile = "three-box" | "two-box" | "one-box" | "pickup";

export interface VehicleDefinition {
  readonly type: VehicleType;
  readonly profile: VehicleProfile;
  readonly length: number;
  readonly width: number;
  readonly height: number;
  readonly mass: number;
  readonly wheelRadius: number;
  readonly wheelbase: number;
  readonly colliderHalfExtents: readonly [x: number, y: number, z: number];
  readonly colliderCenterY: number;
  readonly cameraFocusY: number;
  readonly fixedColor?: number;
  readonly spoiler?: boolean;
  readonly cladding?: boolean;
  readonly roofRails?: boolean;
  readonly slidingTrack?: boolean;
  readonly openBed?: boolean;
}

function definition(
  type: VehicleType,
  profile: VehicleProfile,
  length: number,
  width: number,
  height: number,
  mass: number,
  cues: Pick<
    VehicleDefinition,
    "fixedColor" | "spoiler" | "cladding" | "roofRails" | "slidingTrack" | "openBed"
  > = {},
): VehicleDefinition {
  return Object.freeze({
    type,
    profile,
    length,
    width,
    height,
    mass,
    wheelRadius: THREE.MathUtils.clamp(height * 0.19, 0.27, 0.38),
    wheelbase: length * 0.64,
    colliderHalfExtents: Object.freeze([length * 0.47, height * 0.41, width * 0.46]) as readonly [
      number,
      number,
      number,
    ],
    colliderCenterY: height * 0.43,
    cameraFocusY: Math.min(1.7, height * 0.72),
    ...cues,
  });
}

export const VEHICLE_DEFINITIONS: Readonly<Record<VehicleType, VehicleDefinition>> = Object.freeze({
  compact: definition("compact", "two-box", 3.65, 1.72, 1.48, 1_100),
  hatchback: definition("hatchback", "two-box", 4.1, 1.78, 1.48, 1_250, { spoiler: true }),
  sedan: definition("sedan", "three-box", 4.65, 1.84, 1.46, 1_450),
  coupe: definition("coupe", "three-box", 4.45, 1.86, 1.34, 1_400),
  wagon: definition("wagon", "two-box", 4.75, 1.84, 1.5, 1_550, { roofRails: true }),
  crossover: definition("crossover", "two-box", 4.45, 1.86, 1.65, 1_650, { cladding: true }),
  suv: definition("suv", "two-box", 4.9, 1.98, 1.82, 2_100, {
    cladding: true,
    roofRails: true,
  }),
  minivan: definition("minivan", "one-box", 5, 1.98, 1.8, 1_950, { slidingTrack: true }),
  pickup: definition("pickup", "pickup", 5.35, 1.98, 1.78, 2_200, {
    cladding: true,
    openBed: true,
  }),
  "cargo-van": definition("cargo-van", "one-box", 5.25, 2.02, 2.25, 2_400, {
    cladding: true,
  }),
  taxi: definition("taxi", "three-box", 4.75, 1.86, 1.52, 1_600, {
    fixedColor: 0xf2c94c,
  }),
  police: definition("police", "two-box", 4.95, 1.98, 1.84, 2_300, {
    fixedColor: 0xe5e7e4,
    cladding: true,
    roofRails: true,
  }),
});

export interface VehicleModelInstance {
  group: THREE.Group;
  paintMaterial: THREE.MeshStandardMaterial;
  damagePart: THREE.Object3D;
  wheelPivots: readonly [THREE.Group, THREE.Group];
  resolvedColor: number;
  definition: VehicleDefinition;
}

interface CachedGeometry {
  paint: THREE.BufferGeometry;
  glass: THREE.BufferGeometry;
  trim: THREE.BufferGeometry;
  headlamps: THREE.BufferGeometry;
  tailLamps: THREE.BufferGeometry;
  plates: THREE.BufferGeometry;
  wheels: THREE.BufferGeometry;
  damage: THREE.BufferGeometry;
  livery?: THREE.BufferGeometry;
  taxiSign?: THREE.BufferGeometry;
  policeRed?: THREE.BufferGeometry;
  policeBlue?: THREE.BufferGeometry;
}

interface ProfileVolumes {
  cabinCenter: number;
  cabinLength: number;
  cabinHeight: number;
  cabinBottom: number;
}

function transformed<T extends THREE.BufferGeometry>(
  geometry: T,
  position: readonly [number, number, number],
  rotation: readonly [number, number, number] = [0, 0, 0],
): T {
  geometry.rotateX(rotation[0]);
  geometry.rotateY(rotation[1]);
  geometry.rotateZ(rotation[2]);
  geometry.translate(position[0], position[1], position[2]);
  return geometry;
}

function box(
  size: readonly [number, number, number],
  position: readonly [number, number, number],
  rotation: readonly [number, number, number] = [0, 0, 0],
): THREE.BoxGeometry {
  return transformed(new THREE.BoxGeometry(...size), position, rotation);
}

function rounded(
  size: readonly [number, number, number],
  position: readonly [number, number, number],
  radius: number,
  paint = false,
): THREE.BufferGeometry {
  const geometry = roundedBox(...size, radius);
  if (paint) projectRoundedBoxUvs(geometry);
  return transformed(geometry, position);
}

function mergeParts(
  type: VehicleType,
  parts: THREE.BufferGeometry[],
  useGroups = false,
): THREE.BufferGeometry {
  const compatible = parts.map((part) => (part.index ? part.toNonIndexed() : part));
  let merged: THREE.BufferGeometry | null = null;
  try {
    merged = mergeGeometries(compatible, useGroups);
  } finally {
    for (const part of compatible) part.dispose();
    for (const part of parts) {
      if (!compatible.includes(part)) part.dispose();
    }
  }
  if (!merged) throw new Error(`Failed to merge vehicle geometry: ${type}`);
  return merged;
}

function addMesh(
  group: THREE.Group,
  geometry: THREE.BufferGeometry,
  material: THREE.Material | THREE.Material[],
): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return mesh;
}

export class VehicleModelFactory {
  readonly #textures: VehicleTextures;
  readonly #geometry = new Map<VehicleType, CachedGeometry>();
  readonly #paintTemplate: THREE.MeshStandardMaterial;
  readonly #glassMaterial: THREE.MeshStandardMaterial;
  readonly #trimMaterial: THREE.MeshStandardMaterial;
  readonly #hubMaterial: THREE.MeshStandardMaterial;
  readonly #headlampMaterial: THREE.MeshStandardMaterial;
  readonly #tailLampMaterial: THREE.MeshStandardMaterial;
  readonly #plateMaterial: THREE.MeshStandardMaterial;
  readonly #taxiLiveryMaterial: THREE.MeshStandardMaterial;
  readonly #policeLiveryMaterial: THREE.MeshStandardMaterial;
  readonly #taxiSignMaterial: THREE.MeshStandardMaterial;
  readonly #policeRedMaterial: THREE.MeshStandardMaterial;
  readonly #policeBlueMaterial: THREE.MeshStandardMaterial;
  #disposed = false;

  constructor(textures: VehicleTextures) {
    this.#textures = textures;
    this.#paintTemplate = standardMaterial(0xffffff, 0.5, 0.2, textures.paint);
    this.#glassMaterial = standardMaterial(0x9db1b5, 0.24, 0.35, textures.glass);
    this.#trimMaterial = standardMaterial(0x252a2c, 0.82, 0.02, textures.tire);
    this.#hubMaterial = standardMaterial(0x8a9294, 0.38, 0.72);
    this.#headlampMaterial = standardMaterial(0xfff1cb, 0.3, 0.08);
    this.#headlampMaterial.emissive.set(0xffe3a3);
    this.#tailLampMaterial = standardMaterial(0xb92824, 0.35, 0.05);
    this.#tailLampMaterial.emissive.set(0xd52c27);
    this.#plateMaterial = standardMaterial(0xe7e3cf, 0.55, 0.04);
    this.#plateMaterial.emissive.set(0x5c594e);
    this.#plateMaterial.emissiveIntensity = 0.12;
    this.#taxiLiveryMaterial = this.#decalMaterial(textures.taxiLivery);
    this.#policeLiveryMaterial = this.#decalMaterial(textures.policeLivery);
    this.#taxiSignMaterial = standardMaterial(0xf0b93b, 0.52, 0.04);
    this.#taxiSignMaterial.emissive.set(0x8f5e0b);
    this.#taxiSignMaterial.emissiveIntensity = 0.25;
    this.#policeRedMaterial = standardMaterial(0xd93532, 0.32, 0.08);
    this.#policeRedMaterial.emissive.set(0xa01614);
    this.#policeRedMaterial.emissiveIntensity = 0.45;
    this.#policeBlueMaterial = standardMaterial(0x2d62c4, 0.32, 0.08);
    this.#policeBlueMaterial.emissive.set(0x153c8c);
    this.#policeBlueMaterial.emissiveIntensity = 0.45;
    this.setLightsEnabled(false);
  }

  create(type: VehicleType, color?: number): VehicleModelInstance {
    if (this.#disposed) throw new Error("Vehicle model factory is disposed");
    const definition = VEHICLE_DEFINITIONS[type];
    if (CIVILIAN_TYPES.has(type) && color === undefined)
      throw new Error(`Vehicle color is required for ${type}`);
    if (!CIVILIAN_TYPES.has(type) && color !== undefined)
      throw new Error(`Vehicle color is fixed for ${type}`);
    const resolvedColor = definition.fixedColor ?? color!;
    const geometry = this.#geometry.get(type) ?? this.#cacheGeometry(definition);
    const group = new THREE.Group();
    const paintMaterial = this.#paintTemplate.clone();
    paintMaterial.color.setHex(resolvedColor);
    addMesh(group, geometry.paint, paintMaterial);
    addMesh(group, geometry.glass, this.#glassMaterial);
    addMesh(group, geometry.trim, this.#trimMaterial);
    addMesh(group, geometry.headlamps, this.#headlampMaterial);
    addMesh(group, geometry.tailLamps, this.#tailLampMaterial);
    addMesh(group, geometry.plates, this.#plateMaterial);
    if (geometry.livery)
      addMesh(
        group,
        geometry.livery,
        type === "taxi" ? this.#taxiLiveryMaterial : this.#policeLiveryMaterial,
      );
    if (geometry.taxiSign) addMesh(group, geometry.taxiSign, this.#taxiSignMaterial);
    if (geometry.policeRed) addMesh(group, geometry.policeRed, this.#policeRedMaterial);
    if (geometry.policeBlue) addMesh(group, geometry.policeBlue, this.#policeBlueMaterial);

    const frontAxle = new THREE.Group();
    const rearAxle = new THREE.Group();
    frontAxle.position.set(definition.wheelbase / 2, this.#wheelCenterY(definition), 0);
    rearAxle.position.set(-definition.wheelbase / 2, this.#wheelCenterY(definition), 0);
    addMesh(frontAxle, geometry.wheels, [
      this.#trimMaterial,
      this.#trimMaterial,
      this.#hubMaterial,
      this.#hubMaterial,
    ]);
    addMesh(rearAxle, geometry.wheels, [
      this.#trimMaterial,
      this.#trimMaterial,
      this.#hubMaterial,
      this.#hubMaterial,
    ]);
    group.add(frontAxle, rearAxle);

    const damagePart = addMesh(group, geometry.damage, this.#trimMaterial);
    return {
      group,
      paintMaterial,
      damagePart,
      wheelPivots: [frontAxle, rearAxle],
      resolvedColor,
      definition,
    };
  }

  setLightsEnabled(enabled: boolean): void {
    this.#headlampMaterial.emissiveIntensity = enabled ? 2.4 : 0.05;
    this.#tailLampMaterial.emissiveIntensity = enabled ? 1.6 : 0.05;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    for (const geometry of this.#geometry.values()) {
      for (const value of Object.values(geometry)) value.dispose();
    }
    this.#geometry.clear();
    for (const material of [
      this.#paintTemplate,
      this.#glassMaterial,
      this.#trimMaterial,
      this.#hubMaterial,
      this.#headlampMaterial,
      this.#tailLampMaterial,
      this.#plateMaterial,
      this.#taxiLiveryMaterial,
      this.#policeLiveryMaterial,
      this.#taxiSignMaterial,
      this.#policeRedMaterial,
      this.#policeBlueMaterial,
    ])
      material.dispose();
    disposeVehicleTextures(this.#textures);
  }

  #decalMaterial(map: THREE.Texture): THREE.MeshStandardMaterial {
    const material = standardMaterial(0xffffff, 0.56, 0.02, map);
    material.transparent = true;
    material.alphaTest = 0.08;
    material.depthWrite = false;
    material.polygonOffset = true;
    material.polygonOffsetFactor = -2;
    return material;
  }

  #cacheGeometry(definition: VehicleDefinition): CachedGeometry {
    const geometry = this.#buildGeometry(definition);
    this.#geometry.set(definition.type, geometry);
    return geometry;
  }

  #buildGeometry(definition: VehicleDefinition): CachedGeometry {
    const type = definition.type;
    const paint: THREE.BufferGeometry[] = [];
    const glass: THREE.BufferGeometry[] = [];
    const trim: THREE.BufferGeometry[] = [];
    const headlamps: THREE.BufferGeometry[] = [];
    const tailLamps: THREE.BufferGeometry[] = [];
    const plates: THREE.BufferGeometry[] = [];
    const completed: THREE.BufferGeometry[] = [];
    try {
      const profile = this.#addProfile(definition, paint);
      this.#addGlass(definition, profile, glass);
      this.#addTrim(definition, profile, trim);
      this.#addLampsAndPlates(definition, headlamps, tailLamps, plates);
      const cached: CachedGeometry = {
        paint: mergeParts(type, paint),
        glass: mergeParts(type, glass),
        trim: mergeParts(type, trim),
        headlamps: mergeParts(type, headlamps),
        tailLamps: mergeParts(type, tailLamps),
        plates: mergeParts(type, plates),
        wheels: this.#buildWheels(definition),
        damage: this.#buildDamagePart(definition),
      };
      completed.push(...Object.values(cached));
      if (type === "taxi" || type === "police") cached.livery = this.#buildLivery(definition);
      if (type === "taxi") cached.taxiSign = this.#buildTaxiSign(definition);
      if (type === "police") {
        [cached.policeRed, cached.policeBlue] = this.#buildPoliceLightBar(definition);
      }
      return cached;
    } catch (error) {
      for (const part of [...paint, ...glass, ...trim, ...headlamps, ...tailLamps, ...plates])
        part.dispose();
      for (const geometry of completed) geometry.dispose();
      throw error;
    }
  }

  #addProfile(definition: VehicleDefinition, paint: THREE.BufferGeometry[]): ProfileVolumes {
    const { length, width, height, profile } = definition;
    const rideGap = height * 0.08;
    const bodyHeight = height * 0.42;
    const bodyTop = rideGap + bodyHeight;
    const availableCabinHeight = height - bodyTop;
    paint.push(
      rounded(
        [length * 0.94, bodyHeight, width * 0.94],
        [0, rideGap + bodyHeight / 2, 0],
        0.18,
        true,
      ),
    );

    let cabinCenter: number;
    let cabinLength: number;
    let cabinHeight: number;
    if (profile === "three-box") {
      const hoodLength = length * 0.28;
      cabinLength = length * 0.45;
      const trunkLength = length * 0.22;
      cabinCenter = -length * 0.03;
      cabinHeight = availableCabinHeight * (definition.type === "coupe" ? 0.78 : 0.9);
      paint.push(
        rounded(
          [hoodLength, height * 0.13, width * 0.88],
          [length * 0.32, bodyTop + height * 0.045, 0],
          0.12,
          true,
        ),
        rounded(
          [cabinLength, cabinHeight, width * 0.82],
          [cabinCenter, bodyTop + cabinHeight / 2 - height * 0.035, 0],
          0.2,
          true,
        ),
        rounded(
          [trunkLength, height * 0.14, width * 0.88],
          [-length * 0.37, bodyTop + height * 0.05, 0],
          0.12,
          true,
        ),
      );
    } else if (profile === "two-box") {
      const hoodLength = length * 0.25;
      cabinLength = length * 0.67;
      cabinCenter = -length * 0.1;
      cabinHeight = availableCabinHeight * (definition.type === "compact" ? 0.96 : 0.9);
      paint.push(
        rounded(
          [hoodLength, height * 0.14, width * 0.88],
          [length * 0.34, bodyTop + height * 0.05, 0],
          0.12,
          true,
        ),
        rounded(
          [cabinLength, cabinHeight, width * 0.84],
          [cabinCenter, bodyTop + cabinHeight / 2 - height * 0.035, 0],
          0.2,
          true,
        ),
      );
    } else if (profile === "one-box") {
      const hoodLength = length * 0.12;
      cabinLength = length * 0.8;
      cabinCenter = -length * 0.04;
      cabinHeight = availableCabinHeight * 0.95;
      paint.push(
        rounded(
          [hoodLength, height * 0.12, width * 0.9],
          [length * 0.43, bodyTop + height * 0.04, 0],
          0.1,
          true,
        ),
        rounded(
          [cabinLength, cabinHeight, width * 0.86],
          [cabinCenter, bodyTop + cabinHeight / 2 - height * 0.025, 0],
          0.2,
          true,
        ),
      );
    } else {
      const hoodLength = length * 0.23;
      cabinLength = length * 0.38;
      cabinCenter = length * 0.09;
      cabinHeight = availableCabinHeight * 0.9;
      const bedLength = length * 0.32;
      paint.push(
        rounded(
          [hoodLength, height * 0.14, width * 0.88],
          [length * 0.36, bodyTop + height * 0.05, 0],
          0.12,
          true,
        ),
        rounded(
          [cabinLength, cabinHeight, width * 0.84],
          [cabinCenter, bodyTop + cabinHeight / 2 - height * 0.035, 0],
          0.18,
          true,
        ),
        rounded(
          [bedLength, height * 0.16, width * 0.12],
          [-length * 0.32, bodyTop + height * 0.08, width * 0.39],
          0.06,
          true,
        ),
        rounded(
          [bedLength, height * 0.16, width * 0.12],
          [-length * 0.32, bodyTop + height * 0.08, -width * 0.39],
          0.06,
          true,
        ),
        rounded(
          [height * 0.12, height * 0.16, width * 0.76],
          [-length * 0.48, bodyTop + height * 0.08, 0],
          0.05,
          true,
        ),
      );
    }
    return {
      cabinCenter,
      cabinLength,
      cabinHeight,
      cabinBottom: bodyTop,
    };
  }

  #addGlass(
    definition: VehicleDefinition,
    profile: ProfileVolumes,
    glass: THREE.BufferGeometry[],
  ): void {
    const { width, type } = definition;
    const cargoVan = type === "cargo-van";
    const sideLength = profile.cabinLength * (cargoVan ? 0.3 : 0.78);
    const sideCenter = cargoVan
      ? profile.cabinCenter + profile.cabinLength * 0.25
      : profile.cabinCenter;
    const windowHeight = profile.cabinHeight * (cargoVan ? 0.42 : 0.5);
    const windowY = profile.cabinBottom + profile.cabinHeight * 0.53;
    const sideZ = width * 0.431;
    glass.push(
      box([sideLength, windowHeight, 0.018], [sideCenter, windowY, sideZ]),
      box([sideLength, windowHeight, 0.018], [sideCenter, windowY, -sideZ]),
      box(
        [0.018, windowHeight * 0.92, width * 0.69],
        [profile.cabinCenter + profile.cabinLength * 0.48, windowY, 0],
      ),
    );
    if (!cargoVan)
      glass.push(
        box(
          [0.018, windowHeight * 0.9, width * 0.69],
          [profile.cabinCenter - profile.cabinLength * 0.48, windowY, 0],
        ),
      );
  }

  #addTrim(
    definition: VehicleDefinition,
    profile: ProfileVolumes,
    trim: THREE.BufferGeometry[],
  ): void {
    const { length, width, height, type } = definition;
    const bodyY = height * 0.2;
    trim.push(
      rounded([length * 0.08, height * 0.1, width * 0.9], [-length * 0.48, bodyY, 0], 0.06),
      rounded(
        [height * 0.16, height * 0.1, height * 0.12],
        [
          profile.cabinCenter + profile.cabinLength * 0.34,
          profile.cabinBottom + profile.cabinHeight * 0.52,
          width * 0.5,
        ],
        0.04,
      ),
      rounded(
        [height * 0.16, height * 0.1, height * 0.12],
        [
          profile.cabinCenter + profile.cabinLength * 0.34,
          profile.cabinBottom + profile.cabinHeight * 0.52,
          -width * 0.5,
        ],
        0.04,
      ),
    );
    const doorCount = type === "coupe" || type === "cargo-van" ? 1 : 2;
    for (const side of [-1, 1] as const) {
      for (let index = 0; index < doorCount; index += 1) {
        const doorX = profile.cabinCenter + (index === 0 ? 0.19 : -0.23) * profile.cabinLength;
        trim.push(
          box([height * 0.19, height * 0.025, 0.025], [doorX, height * 0.55, side * width * 0.474]),
        );
      }
      trim.push(
        box(
          [0.022, height * 0.35, 0.025],
          [profile.cabinCenter, height * 0.58, side * width * 0.475],
        ),
      );
    }
    if (definition.cladding)
      trim.push(
        box([length * 0.78, height * 0.13, 0.035], [-length * 0.02, height * 0.19, width * 0.474]),
        box([length * 0.78, height * 0.13, 0.035], [-length * 0.02, height * 0.19, -width * 0.474]),
      );
    if (definition.roofRails)
      trim.push(
        rounded(
          [profile.cabinLength * 0.72, 0.045, 0.045],
          [profile.cabinCenter, height * 0.96, width * 0.31],
          0.02,
        ),
        rounded(
          [profile.cabinLength * 0.72, 0.045, 0.045],
          [profile.cabinCenter, height * 0.96, -width * 0.31],
          0.02,
        ),
      );
    if (definition.spoiler)
      trim.push(
        rounded([length * 0.2, 0.055, width * 0.76], [-length * 0.42, height * 0.91, 0], 0.025),
      );
    if (definition.slidingTrack)
      trim.push(
        box([length * 0.42, 0.035, 0.025], [-length * 0.12, height * 0.58, width * 0.474]),
        box([length * 0.42, 0.035, 0.025], [-length * 0.12, height * 0.58, -width * 0.474]),
      );
    if (definition.openBed)
      trim.push(box([length * 0.3, 0.025, width * 0.66], [-length * 0.32, height * 0.54, 0]));
  }

  #addLampsAndPlates(
    definition: VehicleDefinition,
    headlamps: THREE.BufferGeometry[],
    tailLamps: THREE.BufferGeometry[],
    plates: THREE.BufferGeometry[],
  ): void {
    const { length, width, height } = definition;
    for (const side of [-1, 1] as const) {
      headlamps.push(
        rounded(
          [0.035, height * 0.13, width * 0.2],
          [length * 0.472, height * 0.39, side * width * 0.29],
          0.025,
        ),
      );
      tailLamps.push(
        rounded(
          [0.035, height * 0.14, width * 0.18],
          [-length * 0.472, height * 0.4, side * width * 0.31],
          0.025,
        ),
      );
    }
    plates.push(
      box([0.025, height * 0.12, width * 0.28], [length * 0.478, height * 0.24, 0]),
      box([0.025, height * 0.12, width * 0.28], [-length * 0.478, height * 0.24, 0]),
    );
  }

  #buildWheels(definition: VehicleDefinition): THREE.BufferGeometry {
    const radius = definition.wheelRadius;
    const wheelWidth = definition.width * 0.12;
    const wheelZ = definition.width / 2 - wheelWidth / 2;
    const tireParts = [-1, 1].map((side) =>
      transformed(
        new THREE.CylinderGeometry(radius, radius, wheelWidth, 18, 1),
        [0, 0, side * wheelZ],
        [Math.PI / 2, 0, 0],
      ),
    );
    const hubParts = [-1, 1].map((side) =>
      transformed(
        new THREE.CylinderGeometry(radius * 0.52, radius * 0.52, 0.025, 16),
        [0, 0, side * (definition.width / 2 + 0.012)],
        [Math.PI / 2, 0, 0],
      ),
    );
    return mergeParts(definition.type, [...tireParts, ...hubParts], true);
  }

  #buildDamagePart(definition: VehicleDefinition): THREE.BufferGeometry {
    const { length, width, height, type } = definition;
    if (type !== "police")
      return mergeParts(type, [
        rounded(
          [length * 0.075, height * 0.11, width * 0.9],
          [length * 0.48, height * 0.2, 0],
          0.055,
        ),
      ]);
    return mergeParts(type, [
      rounded([0.08, height * 0.34, width * 0.82], [length * 0.505, height * 0.29, 0], 0.03),
      rounded([0.13, height * 0.42, 0.07], [length * 0.49, height * 0.3, width * 0.32], 0.025),
      rounded([0.13, height * 0.42, 0.07], [length * 0.49, height * 0.3, -width * 0.32], 0.025),
    ]);
  }

  #buildLivery(definition: VehicleDefinition): THREE.BufferGeometry {
    const y = definition.height * 0.5;
    const width = definition.length * 0.56;
    const height = definition.height * 0.3;
    const z = definition.width * 0.477;
    const first = new THREE.PlaneGeometry(width, height);
    projectPlanarUvs(first);
    first.translate(-definition.length * 0.03, y, z);
    const second = new THREE.PlaneGeometry(width, height);
    projectPlanarUvs(second);
    second.rotateY(Math.PI);
    second.translate(-definition.length * 0.03, y, -z);
    return mergeParts(definition.type, [first, second]);
  }

  #buildTaxiSign(definition: VehicleDefinition): THREE.BufferGeometry {
    return mergeParts(definition.type, [
      rounded(
        [definition.length * 0.24, definition.height * 0.13, definition.width * 0.3],
        [-definition.length * 0.04, definition.height * 0.99, 0],
        0.06,
      ),
    ]);
  }

  #buildPoliceLightBar(
    definition: VehicleDefinition,
  ): [THREE.BufferGeometry, THREE.BufferGeometry] {
    const size: readonly [number, number, number] = [
      definition.length * 0.11,
      definition.height * 0.105,
      definition.width * 0.32,
    ];
    const y = definition.height * 1.01;
    return [
      mergeParts(definition.type, [
        rounded(size, [-definition.length * 0.075, y, definition.width * 0.17], 0.035),
      ]),
      mergeParts(definition.type, [
        rounded(size, [-definition.length * 0.075, y, -definition.width * 0.17], 0.035),
      ]),
    ];
  }

  #wheelCenterY(definition: VehicleDefinition): number {
    return definition.height * 0.08 + definition.wheelRadius * 0.92;
  }
}
